import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ChefHat, Sofa, BedDouble, Monitor, Store, Wrench, Bath } from 'lucide-react';

const services = [
	{
		icon: ChefHat,
		title: 'Mobilier de bucătărie',
		desc: 'Bucătării funcționale și elegante, proiectate pe spațiul tău. Fronturi din lemn masiv sau MDF lacuit, cu soluții inteligente de depozitare.',
		img: '/pictures/buc4-main.jpg',
		featured: true,
	},
	{
		icon: Sofa,
		title: 'Mobilier living',
		desc: 'Living-uri care impresionează. Biblioteci, comode, unități TV, canapele și mobilier de living personalizat.',
		img: '/pictures/living5.jpg',
	},
	{
		icon: BedDouble,
		title: 'Mobilier dormitor',
		desc: 'Dormitoare confortabile cu dressinguri, noptiere și paturi realizate pe comandă.',
		img: '/pictures/dormitor13-main.jpeg',
	},
	{
		icon: Monitor,
		title: 'Mobilier de birou',
		desc: 'Spații de lucru productive și ergonomice. Birouri, dulapuri și sisteme de organizare.',
		img: '/pictures/birou.jpg',
	},
	{
		icon: Store,
		title: 'Mobilier comercial',
		desc: 'Amenajări pentru restaurante, hoteluri, birouri corporate și magazine. Design profesional, rezistență maximă.',
		img: 'https://images.unsplash.com/photo-1564278647441-e184eea6801e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBjb21tZXJjaWFsJTIwY3VzdG9tJTIwd29vZCUyMGZ1cm5pdHVyZSUyMHJlc3RhdXJhbnR8ZW58MHx8fHwxNzc2MzU5MDU2fDA&ixlib=rb-4.1.0&q=75&w=600',
	},
	{
		icon: Bath,
		title: 'Mobilier baie',
		desc: 'Mobilier de baie elegant și rezistent — dulapuri, oglinzi și soluții de depozitare adaptate oricărui spațiu.',
		img: '/pictures/baie2.jpg',
	},
	{
		icon: Wrench,
		title: 'Consultanță & Montaj',
		desc: 'Servicii complete: consultanță de design, proiectare, producție și montaj profesional la cheie.',
		img: 'https://images.unsplash.com/photo-1566373924413-3efde9d6eb67?q=80&w=1740&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
	},
];

function ServiceCard({ service, index }) {
	const ref = useRef(null);
	const isInView = useInView(ref, { once: true, margin: '-60px' });
	const Icon = service.icon;

	return (
		<motion.div
			ref={ref}
			initial={{ opacity: 0, y: 24 }}
			animate={isInView ? { opacity: 1, y: 0 } : {}}
			transition={{ duration: 0.55, delay: index * 0.08 }}
			data-testid={`service-card-${index}`}
			className="group relative overflow-hidden rounded-2xl h-72"
		>
			{/* Background */}
			{service.img ? (
				<>
					<img
						src={service.img}
						alt={service.title}
						loading="lazy"
						className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
					/>
					<div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
				</>
			) : (
				<div className="absolute inset-0 bg-[#2C2A29]" />
			)}

			{/* Content — slides up on hover */}
			<div className="relative z-10 h-full flex flex-col justify-end p-7">
				{/* Icon */}
				<div className="w-11 h-11 rounded-full bg-[#A67B5B] flex items-center justify-center mb-4 shadow-lg transition-transform duration-300 group-hover:-translate-y-1">
					<Icon size={20} className="text-white" />
				</div>

				<h3 className="font-['Outfit'] text-xl font-semibold text-white mb-2 leading-tight">
					{service.title}
				</h3>

				{/* Description fades in on hover */}
				<p className="text-sm leading-relaxed text-white/0 group-hover:text-white/80 max-h-0 group-hover:max-h-24 overflow-hidden transition-all duration-500 ease-in-out">
					{service.desc}
				</p>
			</div>

			{/* Subtle border on hover */}
			<div className="absolute inset-0 rounded-2xl ring-0 group-hover:ring-2 group-hover:ring-[#A67B5B]/50 transition-all duration-300 pointer-events-none" />
		</motion.div>
	);
}

export default function ServicesSection() {
	const ref = useRef(null);
	const isInView = useInView(ref, { once: true, margin: '-80px' });

	return (
		<section
			id="servicii"
			data-testid="services-section"
			className="py-20 lg:py-32 bg-[#F9F6F0] dark:bg-[#1A1918]"
		>
			<div className="max-w-7xl mx-auto px-6 lg:px-12">
				{/* Header */}
				<motion.div
					ref={ref}
					initial={{ opacity: 0, y: 20 }}
					animate={isInView ? { opacity: 1, y: 0 } : {}}
					transition={{ duration: 0.6 }}
					className="text-center mb-14"
				>
					<p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#A67B5B] mb-4">
						Serviciile noastre
					</p>
					<h2 className="font-['Outfit'] text-3xl sm:text-4xl tracking-tight font-medium text-[#2C2A29] dark:text-[#F0EDE8]">
						Soluții complete de mobilier
					</h2>
					<p className="mt-4 text-base text-[#6B6561] dark:text-[#9B9590] max-w-xl mx-auto leading-relaxed">
						De la bucătărie la birou — creăm mobilier personalizat care se potrivește
						perfect spațiului și stilului tău.
					</p>
				</motion.div>

				{/* Grid — 3 columns on desktop */}
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
					{services.map((service, i) => (
						<ServiceCard key={service.title} service={service} index={i} />
					))}
				</div>
			</div>
		</section>
	);
}
